import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import configData from '../../config.json';
import moment from 'moment';
import translations from '../../constants/translations.json';
import { useDataContext } from '../../context/data';
import { woltFailureMessageKey } from '../../utils/woltDelivery';
import './styles.css';


const PAGE_SIZE = 20;

const TYPE_FILTERS = [
  { key: 'all',      labelKey: 'orders_filter_all', fallback: 'Alle' },
  { key: 'dine-in',  labelKey: 'orders_dine_in',    fallback: 'Spis her' },
  { key: 'takeaway', labelKey: 'orders_take_away',   fallback: 'Afhent' },
];

// The order history also has the app's Refunded tab
const HISTORY_FILTERS = [
  ...TYPE_FILTERS,
  { key: 'refunded', labelKey: 'orders_status_refunded', fallback: 'Refunderet' },
];

// The app's labels for the backend's paymentType. The app shows MobilePay for
// any type it does not list, which mislabels Apple Pay, Google Pay and Vipps
// epayment orders, so those are named here and an unknown type is shown as sent.
const PAYMENT_TYPES = {
  'Pay later': 'Table Bill',
  'Table Bill': 'Table Bill',
  card: 'Card',
  kevin: 'Kevin',
  vipps: 'Vipps',
  vipps_epayment: 'Vipps',
  mobilepay: 'MobilePay',
  applepay: 'Apple Pay',
  googlepay: 'Google Pay',
};

// A computed amount, rounded to øre
const roundAmount = (value) => Math.round(Number(value || 0) * 100) / 100;

// The app's groomOrder: an item's extras, then each option with its sub-options
const subItemsOf = (item, currency) => {
  const price = (value) => (Number(value) > 0 ? `${currency} ${roundAmount(value)}` : '');
  const attributes = item.attributes || [];
  return [
    ...(item.extras || []).map(extra => ({
      description: `${extra.qty}× ${extra.name} @ ${currency} ${extra.price}`,
      total: price(extra.qty * extra.price),
    })),
    ...attributes.filter(attribute => !attribute.belongsto).flatMap(attribute => [
      {
        description: `${attribute.qty > 1 ? `${attribute.qty}× ` : ''}${attribute.attributeName}: ${attribute.name}`,
        total: price(attribute.additionalPrice),
      },
      ...attributes.filter(sub => sub.belongsto === attribute._id).map(sub => ({
        description: `${sub.attributeName}: ${sub.name}`,
        total: price(sub.additionalPrice),
        isSubAttribute: true,
      })),
    ]),
  ];
};

// How an order's Wolt delivery stands, as the app's orderDeliveryState decides
// it, or null when the order is no delivery. woltDelivery is the backend's
// delivery flag; production's fetch/recent does not send it yet, only the Wolt
// status and tracking URL, so those mark a delivery too.
const deliveryStateOf = (order) => {
  if (!order.woltDelivery && !order.woltDeliveryStatus && !order.woltDeliveryTrackingUrl) return null;
  const status = order.woltDeliveryStatus;
  if (status === 'rejected' || status === 'customer_no_show') return 'failed';
  if (status === 'cancelled' || order.woltDeliveryCancelledAt) return 'cancelled';
  if (order.woltDeliveryState === 'failed') return 'failed';
  if (order.woltDeliveryState === 'pending') return 'pending';
  return 'confirmed';
};

export default function Orders({ history }) {
  const [userProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user_profile')); } catch { return null; }
  });
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [typeFilter, setTypeFilter]   = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [visibleCount, setVisibleCount]   = useState(PAGE_SIZE);

  const { language } = useDataContext();

  const t = (key, fallback) => translations[language]?.[key] || fallback;

  const fetchAllOrders = useCallback(async () => {
    if (!userProfile?.profile) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    // As in the app, fetch/recent holds the orders in progress and the history
    // is fetch/completed and fetch/refunded, each list split by dine-in. A list
    // that fails resolves to null so the others still show.
    const lists = await Promise.all((history ? ['completed', 'refunded'] : ['recent']).flatMap(list =>
      [true, false].map(forDinein =>
        axios.get(`${configData.SERVER_URL}/orders/fetch/${list}?forDinein=${forDinein}&startdate=&enddate=&keyword=`, {
          headers: { Authorization: `Bearer ${userProfile.token}` }
        })
          .then(res => (Array.isArray(res.data) ? res.data : []).map(o => ({
            ...o,
            type: forDinein ? 'dine-in' : 'takeaway',
            tab: list === 'refunded' ? 'refunded' : forDinein ? 'dine-in' : 'takeaway',
          })))
          .catch(e => { console.error(e); return null; })
      )
    ));
    setOrders(lists.filter(Boolean).flat().sort((a, b) => new Date(b.date) - new Date(a.date)));
    if (lists.includes(null)) setError(t('orders_load_error', 'Kunne ikke hente ordrer. Prøv igen.'));
    setLoading(false);
  }, [userProfile, history]);

  useEffect(() => { fetchAllOrders(); }, [fetchAllOrders]);

  const getStatusStyle = (status) => {
    if (!status) return { bg: '#f3f4f6', color: '#666', label: 'N/A' };
    const s = status.toLowerCase();
    if (s === 'completed')              return { bg: '#dcfce7', color: '#166534', label: t('orders_status_completed',  'Fuldført') };
    if (s === 'confirmed')              return { bg: '#dbeafe', color: '#1e40af', label: t('orders_status_confirmed',  'Bekræftet') };
    if (s === 'pending' || s === 'preorder') return { bg: '#e0f2fe', color: '#0369a1', label: t('orders_status_pending', 'Afventer') };
    if (s === 'forpayment')             return { bg: '#fff7ed', color: '#c2410c', label: t('orders_status_payment',   'Afventer betaling') };
    if (s === 'cancelled' || s === 'canceled') return { bg: '#fce4ec', color: '#c62828', label: t('orders_status_cancelled', 'Annulleret') };
    if (s === 'refunded')               return { bg: '#fdf4ff', color: '#7e22ce', label: t('orders_status_refunded',  'Refunderet') };
    return { bg: '#f3f4f6', color: '#666', label: status };
  };

  // Apply filters
  const filtered = orders.filter(o => typeFilter === 'all' || o.tab === typeFilter);

  const visible = filtered.slice(0, visibleCount);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        {history && (
          <Link
            to="/orders"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, color: '#888', textDecoration: 'none' }}
          >
            <i className="fas fa-chevron-left" style={{ fontSize: 11 }} />
            {t('orders_title', 'Mine ordrer')}
          </Link>
        )}
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
          {history ? t('your_completed_orders', 'Din bestillings historik') : t('orders_title', 'Mine ordrer')}
        </h2>
        {!history && (
          <p style={{ fontSize: 14, color: '#888' }}>
            {t('orders_subtitle', 'Se dine tidligere og aktive bestillinger')}
          </p>
        )}
      </div>

      {/* Tabs row — on a narrow phone the tabs scroll sideways, not the page; the
          padding/margin pair keeps the active underline over the row's border */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 1, marginBottom: -1 }}>
          {(history ? HISTORY_FILTERS : TYPE_FILTERS).map(f => (
            <button
              key={f.key}
              onClick={() => { setTypeFilter(f.key); setVisibleCount(PAGE_SIZE); setExpandedOrder(null); }}
              style={{
                padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
                color: typeFilter === f.key ? '#1a1a1a' : '#999',
                borderBottom: typeFilter === f.key ? '2px solid #1a1a1a' : '2px solid transparent',
                marginBottom: -1,
                transition: 'all 0.15s',
              }}
            >
              {t(f.labelKey, f.fallback)}
              {f.key === 'all' && (
                <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 400, color: typeFilter === f.key ? '#555' : '#bbb' }}>
                  {orders.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* The finished orders, one tap away as in the app */}
      {!history && (
        <Link
          to="/order-history"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            padding: '14px 18px', marginBottom: 12, textDecoration: 'none',
            fontSize: 14, fontWeight: 600, color: '#1a1a1a',
          }}
        >
          {t('view_order_history', 'Vis din bestillings historie')}
          <i className="fas fa-chevron-right" style={{ color: '#ccc', fontSize: 12 }} />
        </Link>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
          padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, color: '#991b1b', fontWeight: 600, margin: 0 }}>{error}</p>
          </div>
          <button
            onClick={fetchAllOrders}
            style={{ fontSize: 12, color: '#991b1b', background: 'none', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}
          >
            {t('retry', 'Prøv igen')}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 12, display: 'block' }} />
          {t('loading', 'Indlæser...')}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px', background: 'white',
          borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>📋</span>
          <p style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 4 }}>
            {!history
              ? t('empty_recent_order', 'Der er ingen aktive bestillinger')
              : typeFilter === 'refunded'
                ? t('empty_refunded_order', 'Du har ingen refunderede bestillinger')
                : t('empty_completed_order', 'Der er ingen tidligere bestillinger')}
          </p>
          <p style={{ fontSize: 13, color: '#999' }}>
            {t('orders_empty_description', 'Dine bestillinger vil blive vist her')}
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visible.map((order) => {
              const orderId = order._id || order.refNo;
              const isExpanded = expandedOrder === orderId;
              const status = getStatusStyle(order.status);
              const delivery = deliveryStateOf(order);
              // A failed or cancelled delivery has no courier left to track
              const trackingLink = delivery === 'confirmed' && (order.woltTrackingLink || order.woltDeliveryTrackingUrl);

              return (
                <div key={orderId} style={{
                  background: 'white', borderRadius: 14, overflow: 'hidden',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  {/* Compact row */}
                  <div
                    onClick={() => setExpandedOrder(isExpanded ? null : orderId)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer' }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                      background: order.cover ? `url(${order.cover}) center/cover no-repeat` : '#f3f4f6',
                      backgroundColor: '#f3f4f6',
                    }} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                          {order.restaurant}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, flexShrink: 0,
                          background: order.type === 'dine-in' ? '#ede9fe' : '#fef3c7',
                          color: order.type === 'dine-in' ? '#6d28d9' : '#92400e',
                        }}>
                          {/* A delivery is listed with take-away, but it is not a pickup */}
                          {/* A theater pre-order is take-away only internally — the guest bought a pre-order. */}
                          {order.type === 'dine-in'
                            ? `🍽 ${t('orders_dine_in', 'Spis her')}`
                            : delivery
                              ? `🛵 ${t('orders_delivery', 'Levering')}`
                              : (order.theaterPerformance && order.isAdvanceOrder)
                                ? `🎭 ${t('order_type_theater', 'Forudbestilling')}`
                                : `🛍 ${t('orders_take_away', 'Afhent')}`}
                        </span>
                        {(order.items || []).some(item => item.isDiscounted) && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, flexShrink: 0,
                            background: '#96c11f', color: 'white',
                          }}>
                            heywaitr<span style={{ color: '#E5CA17' }}>+</span>
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {moment(order.date).format('D. MMM YYYY • HH:mm')}
                        {order.refNo && ` · #${order.refNo}`}
                        {order.isAdvanceOrder && order.advanceOrderDate && (
                          <span style={{ marginLeft: 6, color: '#0369a1', fontWeight: 600 }}>
                            📅 {moment(order.advanceOrderDate).format('D. MMM • HH:mm')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 3 }}>
                        {order.currency} {order.totalAmount}
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                        background: status.bg, color: status.color,
                      }}>
                        {status.label}
                      </span>
                    </div>

                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}
                      style={{ color: '#ccc', fontSize: 12, flexShrink: 0 }} />
                  </div>

                  {/* Expanded */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #f0f0f0', padding: '14px 18px', background: '#fafafa' }}>
                      {/* Items */}
                      {(order.items || []).map((item, i) => (
                        <div key={i} style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 13, color: '#555' }}>
                              {item.qty}× {item.name}
                            </span>
                            <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
                              {/* a stamp reward is free; a heywaitr+ price shows the price it replaced */}
                              {item.isReward ? t('voucher_free', 'GRATIS') : (
                                <>
                                  {item.isDiscounted && item.oldPrice > item.price && (
                                    <span style={{ textDecoration: 'line-through', color: '#999', fontWeight: 400, marginRight: 6 }}>
                                      {order.currency} {roundAmount(item.oldPrice * item.qty)}
                                    </span>
                                  )}
                                  {order.currency} {roundAmount(item.price * item.qty)}
                                </>
                              )}
                            </span>
                          </div>
                          {subItemsOf(item, order.currency).map((sub, j) => (
                            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, paddingLeft: sub.isSubAttribute ? 24 : 12 }}>
                              <span style={{ fontSize: 11, color: '#999' }}>{sub.description}</span>
                              <span style={{ fontSize: 11, color: '#999', flexShrink: 0 }}>{sub.total}</span>
                            </div>
                          ))}
                        </div>
                      ))}

                      {/* Fees */}
                      <div style={{ borderTop: '1px solid #e5e7eb', margin: '10px 0', paddingTop: 10 }}>
                        {order.subTotalAmount != null && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#999' }}>{t('subtotal', 'Subtotal')}</span>
                            <span style={{ fontSize: 12, color: '#999' }}>{order.currency} {order.subTotalAmount}</span>
                          </div>
                        )}
                        {order.isLoyaltyDiscounted && Number(order.loyaltyDiscount || 0) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#2e7d32' }}>⭐ {t('loyalty_member_discount', 'Medlemsrabat')}{order.loyaltyDiscountPercent ? ` (${order.loyaltyDiscountPercent}%)` : ''}</span>
                            <span style={{ fontSize: 12, color: '#2e7d32' }}>- {order.currency} {Number(order.loyaltyDiscount).toFixed(2)}</span>
                          </div>
                        )}
                        {Number(order.discountCodeAmount || 0) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#2e7d32' }}>🏷️ {t('discount_code_row', 'Rabatkode')}{order.discountCode ? ` ${order.discountCode}` : ''}</span>
                            <span style={{ fontSize: 12, color: '#2e7d32' }}>- {order.currency} {Number(order.discountCodeAmount).toFixed(2)}</span>
                          </div>
                        )}
                        {/* fee is the amount on every list; fetch/refunded sends the percentage as serviceFee */}
                        {Number(order.fee) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#999' }}>{t('orders_service_fee', 'Servicegebyr')}</span>
                            <span style={{ fontSize: 12, color: '#999' }}>{order.currency} {order.fee}</span>
                          </div>
                        )}
                        {order.tipAmount > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#999' }}>{t('orders_tips', 'Drikkepenge')}</span>
                            <span style={{ fontSize: 12, color: '#999' }}>{order.currency} {order.tipAmount}</span>
                          </div>
                        )}
                        {order.bagTakeAwayPrice > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#999' }}>{t('orders_carry_bag', 'Pose')}</span>
                            <span style={{ fontSize: 12, color: '#999' }}>{order.currency} {order.bagTakeAwayPrice}</span>
                          </div>
                        )}
                        {order.deliveryFee > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#999' }}>{t('orders_delivery_fee', 'Levering')}</span>
                            <span style={{ fontSize: 12, color: '#999' }}>{order.currency} {order.deliveryFee}</span>
                          </div>
                        )}
                      </div>

                      {/* Total */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{t('total', 'Total')}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#96c11f' }}>{order.currency} {order.totalAmount}</span>
                      </div>

                      {/* Wolt delivery, as the app shows it on every order: where it
                          goes, then a failed or cancelled one says so instead of
                          showing a dead link; ETA + tracking while it is on its way */}
                      {delivery && order.woltDropoffAddress && (
                        <p style={{ fontSize: 12, color: '#555', margin: '12px 0 0' }}>
                          📍 {order.woltDropoffAddress}
                        </p>
                      )}
                      {(delivery === 'failed' || delivery === 'cancelled') && (
                        <p style={{ fontSize: 12, color: '#c62828', margin: '12px 0 0', fontWeight: 600 }}>
                          {t(woltFailureMessageKey(order), "We couldn't complete this delivery")}
                        </p>
                      )}
                      {delivery === 'pending' && (
                        <p style={{ fontSize: 12, color: '#555', margin: '12px 0 0' }}>
                          {t('wolt_delivery_pending_message', 'Waiting for a courier to confirm your delivery.')}
                        </p>
                      )}
                      {order.status === 'confirmed' && delivery === 'confirmed' && order.woltDeliveryDropoffETA && (
                        <p style={{ fontSize: 12, color: '#555', margin: '12px 0 0', fontWeight: 600 }}>
                          🕐 {t('orders_delivery_eta', 'Forventet levering ca.')}{' '}
                          {new Date(order.woltDeliveryDropoffETA).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                      {trackingLink && (
                        <div>
                          <a
                            href={trackingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                              padding: '8px 16px', fontSize: 12, fontWeight: 600, color: '#fff',
                              background: '#00c2e8', borderRadius: 10, textDecoration: 'none',
                            }}
                          >
                            🛵 {t('orders_track_delivery', 'Følg din levering')}
                          </a>
                        </div>
                      )}

                      {/* Receipt link */}
                      {order.receiptURL && (
                        <a
                          href={order.receiptURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12,
                            fontSize: 12, color: '#1e40af', fontWeight: 600, textDecoration: 'none',
                          }}
                        >
                          🧾 {t('orders_view_receipt', 'Se kvittering')}
                          {order.receiptNumber && ` #${order.receiptNumber}`}
                        </a>
                      )}

                      {/* Payment type */}
                      {order.paymentType && (
                        <p style={{ fontSize: 12, color: '#999', margin: '8px 0 0' }}>
                          {t('payment_type_label', 'Betalingstype')}: {PAYMENT_TYPES[order.paymentType] || order.paymentType}
                        </p>
                      )}

                      {/* Table number */}
                      {order.tablenumber && (
                        <p style={{ fontSize: 12, color: '#999', margin: '8px 0 0' }}>
                          {t('table_number', 'Bord')}: {order.tablenumber}
                        </p>
                      )}

                      {/* Notes */}
                      {order.notes && (
                        <p style={{ fontSize: 12, color: '#999', margin: '6px 0 0', fontStyle: 'italic' }}>
                          📝 {order.notes}
                        </p>
                      )}

                      {/* Re-order where the app offers it: an order in progress or
                          a finished take-away, at a venue that still exists */}
                      {(!history || order.tab === 'takeaway') && order.restaurantId && (
                        <div>
                          <Link
                            to={`/restaurants/${order.restaurantId}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12,
                              padding: '8px 16px', fontSize: 12, fontWeight: 600, color: '#fff',
                              background: '#96c11f', borderRadius: 10, textDecoration: 'none',
                            }}
                          >
                            🔁 {t('reorder', 'Genbestil')}
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Load more */}
          {visibleCount < filtered.length && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button
                onClick={() => setVisibleCount(v => v + PAGE_SIZE)}
                style={{
                  padding: '10px 28px', borderRadius: 20, border: '1px solid #e5e7eb',
                  background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  color: '#555',
                }}
              >
                {t('orders_load_more', 'Vis flere')} ({filtered.length - visibleCount} {t('orders_remaining', 'tilbage')})
              </button>
            </div>
          )}

          {/* Count */}
          <p style={{ textAlign: 'center', fontSize: 12, color: '#ccc', marginTop: 16 }}>
            {Math.min(visibleCount, filtered.length)} / {filtered.length} {t('orders_shown', 'ordrer')}
          </p>
        </>
      )}
    </div>
  );
}
